import { useIsMutating } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import {
  FormProvider,
  useController,
  useForm,
  useFormContext,
} from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import userImg from "../../assets/images/user.svg";
import Input from "../../components/CommonComponents/Input/Input";
import { NavbarApplicationInfo } from "../../components/CommonComponents/Navbar/NavbarApplicationInfo";
import { tableCustomStyles } from "../../components/CommonComponents/Table/Table";
import { RenderField } from "../../components/RenderField/RenderField";
import { ErrorToast } from "../../components/toastMessage";
import {
  TPopulatedForm,
  useApplication,
  useUploadFile,
} from "../../hooks/applications.hooks";
import { useToggle } from "../../hooks/common";
import { TDocumentConfig } from "../../hooks/forms.hooks";
import {
  useAllApplicationReviews,
  useNOCForm,
  useResubmissionData,
  useResubmitApplication,
  useSaveResubmissionData,
} from "../../hooks/resubmission.hooks";
import { cleanseObject, openInNewTab } from "../../utils/common";
import { formatDate } from "../../utils/date";
import { RenderAllReviews } from "../LevelTwo/RenderAllReviews";
import { RenderApplication } from "./RenderApplication";
// import axios from "axios";
import { BASE_URL } from "../../constants/Constants";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import { _axios } from "../../utils/axios";

function RenderNOC({
  step,
  maxAllowed,
}: {
  maxAllowed: number;
  step: TPopulatedForm;
}) {
  const [nocsUpdated, setNocsUpdated] = useState<number>(0);
  const uploadMutation = useUploadFile();
  const [inc, setInc] = useState(1);
  const formMethods = useFormContext();
  const isMutating = useIsMutating() > 0;
  const { control, setError, getValues, resetField } = formMethods;

  const nocDocument = useController({ control, name: "noc_document" });

  const uploadedNOC = useController({
    control,
    name: "nocs",
    rules: {
      validate: (val) =>
        (val as []).length === maxAllowed ||
        `Please upload ${maxAllowed} NOC(s).`,
    },
  });

  const { onChange: setUploadedNOC, value: uploadedNOCValue } =
    uploadedNOC.field;

  const onUpload = async () => {
    try {
      const { nocs: _, noc_document, ...rest } = getValues();

      if (noc_document instanceof File) {
        const result = await uploadMutation.mutateAsync(noc_document);
        const noc_doc = result?.data?.data;
        if (!noc_doc) throw new Error();

        const newVal = [
          ...((uploadedNOCValue as []) || []),
          { ...cleanseObject(rest), noc_doc },
        ];

        setUploadedNOC(newVal);
        nocDocument.field.onChange(null);
        setInc((e) => e + 1);
        setNocsUpdated((p) => ++p);
        (step.fields || []).forEach((e) =>
          resetField(e.uid, { defaultValue: "" })
        );
      } else setError("noc_document", { message: "* Required" });
    } catch (err) {
      ErrorToast("Something went wrong while uploading file!");
    }
  };

  const columns = useMemo(
    (): TableColumn<Record<string, unknown>>[] => [
      { name: "Sr. No.", selector: (_, i) => (i || 0) + 1, center: true },
      {
        name: "NOC Type",
        selector: (d) =>
          String(
            (
              step.fields?.find((e) => e.uid === "noc_type")?.options || []
            ).find((e) => e.id === d.noc_type)?.name || "-"
          ),
        center: true,
      },
      {
        name: "NOC No",
        selector: (d) => String(d?.noc_number || "-"),
        center: true,
      },
      {
        name: "NOC Date",
        cell: (d) => (
          <div className="break-words min-w-6">
            {formatDate(String(d?.noc_date || "x"))}
          </div>
        ),
        center: true,
      },
      {
        name: "Edit",
        center: true,
        cell: (d, i) => (
          <div className="space-x-4">
            <button
              className="text-lg"
              onClick={() => openInNewTab(String(d?.noc_doc))}
            >
              <i className="m-0 fa fa-eye"></i>
            </button>
            <button
              className="text-lg"
              onClick={() =>
                setUploadedNOC(
                  (uploadedNOCValue as []).filter((_, index) => index !== i)
                )
              }
            >
              <i className="m-0 fa fa-trash"></i>
            </button>
          </div>
        ),
      },
    ],
    [step, uploadedNOCValue, setUploadedNOC]
  );
  const length = ((uploadedNOC.field.value as Record<string, unknown>[]) || [])
    ?.length;
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-10 mt-4">
        {/* {(step?.fields || [])?.map((field) => ( */}
        {(step?.fields || [])?.filter(field => field.label !== "NOC Document").map((field) => (

          
          <div key={`field-${field.id}`}>
            <RenderField disabled={maxAllowed <= length} field={field} />
          </div>
        ))}
        <Input
          type="file"
          label="NOC Document"
          key={`noc-doc-${inc}`}
          ref={nocDocument.field.ref}
          disabled={maxAllowed <= length}
          onBlur={nocDocument.field.onBlur}
          error={!!nocDocument.formState?.errors?.noc_document}
          onChange={(e) => nocDocument.field.onChange(e.target.files?.[0])}
          helperText={nocDocument.formState?.errors?.noc_document?.message?.toString()}
          className="file:-my-3 file:-ml-4 file:h-[50px] file:border-0 file:mr-4 file:bg-primary file:px-4"
        />
      </div>
      <div className="flex justify-center items-center my-8">
        <button
          type="button"
          onClick={onUpload}
          disabled={maxAllowed <= length || isMutating}
          className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300"
        >
          {uploadMutation.isPending ? "Uploading" : "Upload NOC"}
        </button>
      </div>
      <div className="mx-auto max-w-[800px]">
        {!!length && (
          <DataTable
            keyField="url"
            className="mt-4"
            columns={columns}
            customStyles={tableCustomStyles}
            data={(uploadedNOC.field.value as Record<string, unknown>[]) || []}
          />
        )}
        <p className="mt-1 text-red-500 text-sm">
          {uploadedNOC?.fieldState?.error?.message?.toString?.() ||
            (nocsUpdated === maxAllowed
              ? ""
              : "* The following NOC(s) were declined, please delete and reupload the same.")}
        </p>
      </div>
    </>
  );
}

function RenderDocumentConfig({
  config,
  update,
}: {
  update: (x: string) => unknown;
  config: TDocumentConfig;
}) {
  const uploadMutation = useUploadFile();
  const document = useController({
    name: `documents.${config.document_config_id}`,
    rules: { required: config.mandatory ? "* Required" : false },
  });

  const onUpload = async (file: File) => {
    try {
      if (config.file_type) {
        const types = config.file_type.split(",");
        if (!types.some((e) => file.type.includes(e))) {
          return ErrorToast(
            "Invalid file select, please select a different file!"
          );
        }
      }
      if (
        config.file_size &&
        ["KB", "MB"].includes(config?.file_size_unit || "")
      ) {
        let size = config.file_size;
        const sizeInMB = file.size / (1024 * 1024);
        if (config.file_size_unit === "KB") {
          size /= 1024;
        }

        if (sizeInMB > size) {
          return ErrorToast(
            `Please select a small file, file cannot be more than ${config.file_size} ${config.file_size_unit}`
          );
        }
      }

      const result = await uploadMutation.mutateAsync(file);
      const url = result?.data?.data;
      update(String(config.document_config_id));
      if (!url) throw new Error();
      document.field.onChange(url);
    } catch (err) {
      ErrorToast("Something went wrong while uploading file!");
    }
  };

  return (
    <div className="flex justify-between items-center">
      <div className="basis-3/4">
        <p className="text-grey-600">
          {config.document_label}
          {config.mandatory ? <span className="text-red-500"> *</span> : null}
        </p>
      </div>
      <div className="flex space-x-6 items-center justify-center">
        {!document.field?.value ? (
          <Input
            type="file"
            ref={document.field.ref}
            onBlur={document.field.onBlur}
            disabled={uploadMutation.isPending}
            error={!!document.fieldState?.error}
            onChange={(e) => {
              const hit = e.target.files?.[0];
              if (hit) onUpload(hit);
            }}
            helperText={document?.fieldState?.error?.message?.toString()}
            className="file:-my-3 file:-ml-4 file:h-[50px] file:border-0 file:mr-4 file:bg-primary file:px-4"
          />
        ) : (
          <>
            <button onClick={() => openInNewTab(document.field.value)}>
              <i className="text-xl m-0 fa fa-eye"></i>
            </button>
            <button onClick={() => document.field.onChange(null)}>
              <i className="text-xl m-0 fa fa-trash-o"></i>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ResubmissionForm({
  resubmissionInfo: data,
  application,
  nocForm,
}: {
  nocForm?: TPopulatedForm;
  resubmissionInfo: NonNullable<ReturnType<typeof useResubmissionData>["data"]>;
  application: NonNullable<ReturnType<typeof useApplication>["data"]>;
}) {
  const [docsUpdated, setDocsUpdated] = useState<string[]>([]);
  const submitMutation = useResubmitApplication();
  const saveMutation = useSaveResubmissionData();
  const mutating = useIsMutating() > 0;
  const navigate = useNavigate();
  const [reviewDialog, toggleReviewDialog] = useToggle();
  const [previewDialog, togglePreviewDialog] = useToggle();
  const nocNeeded = !!data?.nocs?.length;
  const allReviewsQuery = useAllApplicationReviews({ id: application.id });

  const methods = useForm({
    defaultValues: {
      ...(data.fields || []).reduce<Record<string, unknown>>((acc, e) => {
        acc[e.uid] = application[e.uid];
        return acc;
      }, {}),
      nocs: data?.nocs || [],
      noc_number: "",
      noc_date: "",
      noc_type: "",
      noc_document: "",
      documents: (data.docConfigs || []).reduce<Record<string, unknown>>(
        (acc, e) => {
          const hit = (data.docs || []).find(
            (e) => String(e.document_config_id) === String(e.document_config_id)
          );
          acc[e.document_config_id] = hit?.document || "";
          return acc;
        },
        {}
      ),
    },
  });

  if (nocNeeded && !nocForm) {
    return (
      <div className="flex justify-center items-center p-8">
        <p>Something went wrong while fetching application info!</p>
      </div>
    );
  }

  let disableBtns = mutating;
  const { formState } = methods;

  const onSave: Parameters<typeof methods.handleSubmit>[0] = async (data) => {
    const {
      documents,
      nocs,
      noc_date: _,
      noc_document: __,
      noc_number: ___,
      noc_type: ____,
      ...fields
    } = data;

    const saveResult = await saveMutation.mutateAsync({
      applicationId: application.id,
      resubmissionData: {
        nocs: nocs,
        docs: documents,
        fields: cleanseObject(fields),
      },
    });

    // if (saveResult.data?.success) {
    //   try {
    //     await axios.post(
    //       `${BASE_URL}/api/bellMessage/application/update-all-notifications`,
    //       { application_id: application.id }
    //     );
    //   } catch (error) {
    //     ErrorToast("Failed to update notifications");
    //   }
    // }
  };

  const onUpdateDoc = (configId: string) => {
    setDocsUpdated((p) => (p.includes(configId) ? p : p.concat(configId)));
  };

  const onSubmit = async () => {
    try {
      if (!formState.isValid) {
        const x = await methods.trigger(undefined, { shouldFocus: true });
        if (!x) return;
      }

      const {
        documents,
        nocs,
        noc_date: _,
        noc_document: __,
        noc_number: ___,
        noc_type: ____,
        ...fields
      } = methods.getValues();

      const result = await saveMutation.mutateAsync({
        applicationId: application.id,
        resubmissionData: {
          nocs: nocs,
          docs: documents,
          fields: cleanseObject(fields),
        },
      });

      if (!result.data?.success) return;

      const result2 = await submitMutation.mutateAsync({
        applicationId: application.id,
      });

      if (!result2.data.success) throw new Error();
      try {
        const notificationResponse = await _axios.post(
          `${BASE_URL}/api/bellMessage/application/update-all-notifications`,
          { application_id: application.applicationId }
        );

        if (notificationResponse.status !== 200) {
          console.warn("Notification update did not return a 200 status");
        }
      } catch (error) {
        console.error("Failed to update notifications:", error);
        // Don't show an error toast for this, as it's not critical
      }

      return navigate("/user-applications");
    } catch (err) {
      //no-op
    }
  };
  return (
    <FormProvider {...methods}>
      <div className="px-4 pb-4 md:p-4">
        <form
          noValidate
          className="bg-white p-8 rounded-xl shadow-lg border border-gray-300"
          onSubmit={methods.handleSubmit(onSave)}
        >
          <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
            Application Review
          </h4>
          <div className="flex justify-end">
            <button
              type="button"
              className="text-base font-bold disabled:text-gray-500"
              disabled={!allReviewsQuery.data?.length}
              onClick={toggleReviewDialog}
            >
              <i className="fa fa-history"></i>
              Previous Reviews
            </button>
          </div>
          <hr className="my-4" />
          <div className="flex justify-between space-x-4">
            <div className="flex shrink-0 flex-col items-center">
              <img
                src={userImg}
                className="h-10 w-10 md:h-16 md:w-16"
                alt="profile-pic"
              />
            </div>
            <div className="grow px-1 py-1">
              <div className="flex justify-between mb-2">
                <h6 className="text-lg font-bold">
                  {data?.review.reviewer?.name || "--"}
                  <span className="font-normal inline-block ml-4">
                    {data?.review?.reviewer?.role || ""}
                  </span>
                </h6>
                <h6 className="hidden md:block text-base font-bold">
                  {`Date - ${formatDate(data?.review.date || "")}`}
                </h6>
              </div>
              <p>{data?.review.comment_text}</p>
            </div>
          </div>
          {!!(data?.fields || []).length && <hr className="my-4" />}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-10">
            {(data?.fields || [])?.map((field) => (
              <div key={`field-${field.id}`}>
                <RenderField field={{ ...field, mandatory: true }} />
              </div>
            ))}
          </div>

          {!!(nocNeeded && nocForm) && (
            <>
              <hr className="my-4" />
              <h5 className="text-grey-600 font-semibold text-lg">NOC(s)</h5>
              <RenderNOC maxAllowed={data?.nocs?.length ?? 0} step={nocForm} />
            </>
          )}

          <div className="space-y-4">
            {!!data?.docConfigs?.length && (
              <>
                <hr className="my-4" />

                <h5 className="text-grey-600 font-semibold text-lg">
                  Document(s)
                </h5>
              </>
            )}
            {(data?.docConfigs || []).map((e) => (
              <RenderDocumentConfig
                config={e}
                update={onUpdateDoc}
                key={`doc-config-${e.document_config_id}`}
              />
            ))}
            {!!data?.docConfigs?.length &&
              docsUpdated.length !== data.docConfigs.length && (
                <p className="mt-1 text-red-500 text-sm">
                  * The following documents(s) were declined, please delete and
                  reupload the same.
                </p>
              )}
          </div>
          <div className="flex justify-center flex-wrap md:flex-nowrap items-center gap-6 mt-6">
            <button
              type="button"
              disabled={disableBtns}
              onClick={() => navigate(-1)}
              className="bg-black px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 min-w-32"
            >
              Back
            </button>
            <button
              type="button"
              onClick={togglePreviewDialog}
              className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 min-w-32"
            >
              Preview
            </button>
            <button
              type="submit"
              disabled={disableBtns}
              className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 min-w-32"
            >
              {saveMutation.isPending ? "Saving" : "Save"}
            </button>
            <button
              type="button"
              onClick={onSubmit}
              disabled={disableBtns}
              className="bg-primary px-6 py-2 rounded-full font-bold text-white disabled:bg-gray-300 min-w-32"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
      {reviewDialog && (
        <RenderAllReviews
          onClose={toggleReviewDialog}
          reviews={allReviewsQuery.data ?? []}
        />
      )}
      {previewDialog && (
        <RenderApplication
          onClose={togglePreviewDialog}
          application={{
            ...application,
            ...cleanseObject(methods.getValues()),
          }}
        />
      )}
    </FormProvider>
  );
}

export default function ResubmitApplication() {
  const location = useLocation();

  const applicationQuery = useApplication({
    id: +location.state.id,
  });

  const infoQuery = useResubmissionData(String(location.state.id));
  const nocForm = useNOCForm(!!infoQuery.data?.nocs?.length);
  const id = parseInt(location.state.id || -1);

  const setStuff = useSetStuffToRenderOnNavbar();

  const application = applicationQuery.data;

  useEffect(() => {
    if (!application) return () => null;
    setStuff(
      <NavbarApplicationInfo
        applicationId={application?.applicationId}
        status={application?.status}
        applicationType={application?.rl_application_type}
        currentPhase={application?.current_phase}
      />
    );
    return () => setStuff(null);
  }, [setStuff, application]);

  if (isNaN(id) || id < 0) {
    return <Navigate to="/user-applications" />;
  }

  const isFetching =
    infoQuery.isFetching || applicationQuery.isFetching || nocForm.isFetching;
  const isError =
    infoQuery.isError || applicationQuery.isError || nocForm.isError;

  if (isFetching || isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching application info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  if (!applicationQuery.data || !infoQuery.data) {
    return (
      <div className="flex justify-center items-center p-8">
        <p>Something went wrong while fetching application info!</p>
      </div>
    );
  }

  return (
    <ResubmissionForm
      nocForm={nocForm.data?.data}
      application={applicationQuery.data}
      resubmissionInfo={infoQuery.data}
    />
  );
}
